import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from './api'
import CookieManager from '../src/lib/CookiesManager'
import dayjs from 'dayjs'
import { User } from '@/interfaces/user'

export class SmsService {
  private otpSid: string = ''

  getOtpSid(): string {
    return this.otpSid
  }

  async getAllPhoneNumbers(): Promise<string[]> {
    try {
      const token = CookieManager.getToken()
      if (!token) {
        throw new Error('No token found')
      }

      const response = await fetch(`${API_BASE_URL}/all/full`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'x-api-key': X_API_KEY,
        },
      })

      if (!response.ok) {
        const errorData = await response.text()
        console.error('Error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorData,
        })
        throw new Error(
          `Server responded with ${response.status}: ${errorData}`,
        )
      }

      const data = await response.json()

      if (!data.users) {
        throw new Error('Invalid response format - no users property')
      }

      const phoneNumbers = data.users
        .map((user: User) => user.phone)
        .filter(
          (phone: string | undefined): phone is string =>
            phone !== undefined && phone !== null && phone !== '',
        )

      return phoneNumbers
    } catch (error: any) {
      console.error('Error fetching phone numbers:', error)
      Sentry.captureException(error, {
        extra: {
          action: 'getAllPhoneNumbers',
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'getAllPhoneNumbers',
        },
      })
      throw error
    }
  }

  async requestOTP(phone: string, formData: any): Promise<any> {
    try {
      const token = CookieManager.getToken()
      if (!token) {
        throw new Error('No token found')
      }

      const preparedData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        nickname: formData.nickname,
        email: formData.email,
        tva: formData.tva || '',
        birthday: dayjs(formData.birthday, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        address: {
          street: formData.address.street || '',
          number: formData.address.number || '',
          city: formData.address.city || '',
          zip_code: formData.address.zip_code || '',
        },
        status: Array.isArray(formData.status)
          ? formData.status
          : [formData.status],
        phone: phone,
      }
      console.log('Sending PATCH data:', JSON.stringify(preparedData, null, 2))

      const response = await fetch(`${API_BASE_URL}/user/${formData.userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify(preparedData),
      })

      const responseText = await response.text() // Capture la réponse brute
      console.error(
        'PATCH Response:',
        response.status,
        response.statusText,
        responseText,
      )

      const responseData = JSON.parse(responseText) // Parse seulement si la réponse est OK

      if (responseData.otpSid) {
        localStorage.setItem('verificationSid', responseData.otpSid)
        this.otpSid = responseData.otpSid
      }

      return {
        success: true,
        otpSid: responseData.otpSid,
      }
    } catch (error: any) {
      Sentry.captureException(error, {
        extra: {
          action: 'requestOTP',
          phone,
          formData,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'requestOTP',
        },
      })
      throw error
    }
  }

  async verifyOTP(phone: string, otp: string): Promise<any> {
    const token = CookieManager.getToken()
    if (!token) throw new Error('No token found')

    try {
      const response = await fetch(`${API_BASE_URL}/user/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify({
          phone,
          otpCode: otp,
        }),
      })

      const responseData = await response.json()

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to verify OTP')
      }

      localStorage.removeItem('verificationSid')

      return responseData
    } catch (error: any) {
      Sentry.captureException(error, {
        extra: {
          action: 'verifyOTP',
          phone,
          otp,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'verifyOTP',
        },
      })
      throw error
    }
  }
}

export const smsService = new SmsService()
